import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { UserPlus, Upload, MessageSquare, Search, Heart, Phone, ShieldCheck, ArrowRight } from 'lucide-react';

export default function HowItWorksPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('howItWorks');

  const sellerSteps = [
    { icon: UserPlus, title: t('sellerStep1'), desc: t('sellerStep1Desc') },
    { icon: Upload, title: t('sellerStep2'), desc: t('sellerStep2Desc') },
    { icon: MessageSquare, title: t('sellerStep3'), desc: t('sellerStep3Desc') },
  ];
  const buyerSteps = [
    { icon: Search, title: t('buyerStep1'), desc: t('buyerStep1Desc') },
    { icon: Heart, title: t('buyerStep2'), desc: t('buyerStep2Desc') },
    { icon: Phone, title: t('buyerStep3'), desc: t('buyerStep3Desc') },
  ];

  const renderSteps = (steps) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {steps.map((step, i) => (
        <div key={i} className="relative bg-white border rounded-xl p-6 text-center hover:shadow-sm transition-all">
          <div className="absolute top-3 right-3 text-3xl font-heading font-bold text-neutral-100">{i + 1}</div>
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#1F3F72] text-white mb-4">
            <step.icon className="w-5 h-5" />
          </div>
          <h3 className="font-heading text-sm font-semibold text-neutral-900 mb-2">{step.title}</h3>
          <p className="text-xs text-neutral-500 leading-relaxed">{step.desc}</p>
        </div>
      ))}
    </div>
  );

  return (
    <>
    <SEO title="Comment ça marche" description="Découvrez comment acheter et vendre du matériel professionnel de déstockage sur DestockPro en 3 étapes simples." path="/comment-ca-marche" />
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="how-it-works-page">
      <section className="text-center mb-14">
        <h1 className="font-heading text-4xl font-bold text-neutral-900 mb-3">{t('title')}</h1>
        <p className="text-base text-neutral-500 max-w-xl mx-auto">{t('subtitle')}</p>
      </section>

      {/* Seller */}
      <section className="mb-16" data-testid="seller-steps">
        <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-2">{t('sellerTitle')}</h2>
        <p className="text-sm text-neutral-500 mb-8">{t('sellerSubtitle')}</p>
        {renderSteps(sellerSteps)}
        <div className="text-center mt-8">
          <Button className="bg-[#F25C05] hover:bg-[#d94f04] text-white" onClick={() => navigate('/publier')} data-testid="hiw-seller-cta">
            {t('createSellerAccount')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Buyer */}
      <section className="mb-16" data-testid="buyer-steps">
        <h2 className="font-heading text-2xl font-bold text-neutral-900 mb-2">{t('buyerTitle')}</h2>
        <p className="text-sm text-neutral-500 mb-8">{t('buyerSubtitle')}</p>
        {renderSteps(buyerSteps)}
        <div className="text-center mt-8">
          <Button variant="outline" onClick={() => navigate('/annonces')} data-testid="hiw-buyer-cta">
            {t('browseListings')} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Trust section */}
      <section className="bg-[#1F3F72] text-white rounded-2xl p-8 text-center" data-testid="hiw-trust">
        <ShieldCheck className="w-8 h-8 text-blue-200 mx-auto mb-3" />
        <h2 className="font-heading text-xl font-bold mb-2">{t('trustTitle')}</h2>
        <p className="text-sm text-blue-200 leading-relaxed max-w-xl mx-auto">{t('trustText')}</p>
      </section>
    </div>
    </>
  );
}